<template>
  <div class="setup-step-1">
    <Card flat>
      <template #title>
        <v-skeleton-loader
          v-if="pageLoading"
          type="heading"
          width="100%"
          height="28px"
          class="mt-1 mb-1"
        />
        <h5
          v-else
          class="mt-2 mb-2"
        >
          Step 1: Configure your SSO connection using Datapeople's details below:
        </h5>
      </template>

      <template #text>
        <div class="mt-8">
          <v-skeleton-loader
            v-if="pageLoading"
            type="text@2"
            width="100%"
            height="40"
          />
          <ul
            v-else
            class="pl-6"
          >
            <li>
              The user’s unique IdP identifier should be mapped as “NameId” in the assertion.
            </li>
            <li>
              The SAML assertion should also include the following attributes,
              named exactly as below:
            </li>
          </ul>
        </div>

        <div class="mt-4 mb-6">
          <v-skeleton-loader
            v-if="pageLoading"
            type="sentences@4,text"
            width="100%"
            height="194"
          />
          <v-data-table
            v-else
            :must-sort="false"
            :headers="[
              { title: 'Attribute Name', value: 'name' },
              { title: 'Contents', value: 'contents' },
            ]"
            :items="[
              { name: 'email', contents: 'User email address' },
              { name: 'firstName', contents: 'User first name' },
              { name: 'lastName', contents: 'User last name' },
            ]"
          >
            <template #bottom>
              <span />
            </template>
          </v-data-table>
        </div>

        <div
          v-for="link in links"
          :key="link.label"
        >
          <div v-if="pageLoading">
            <v-skeleton-loader
              type="text"
              width="100"
              height="30"
            />
            <v-skeleton-loader
              type="text"
              width="100%"
              height="56"
            />
          </div>
          <DpplTextField
            v-else
            :label="link.label"
            disabled
            :background-color="grey400"
            :model-value="link.value"
          >
            <template #append>
              <v-btn
                class="active-button"
                icon
                variant="flat"
                rounded="0"
              >
                <v-icon
                  icon="$content-copy"
                  @click="copyToClipboard({ content: link.value })"
                />
              </v-btn>
            </template>
          </DpplTextField>
        </div>

        <div class="mt-2">
          <v-skeleton-loader
            v-if="pageLoading"
            type="text"
            width="350px"
            height="40px"
          />
          <div v-else>
            Datapeople Metadata file
            <a
              class="ml-6"
              :href="metadataUrl"
              download="datapeople-metadata.xml"
              title="Download metadata file"
            >
              <DpplAutoSizedButton color="textDarkest">
                Download
              </DpplAutoSizedButton>
            </a>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useSettingsSsoSetup } from '@/stores/settings/ssoSetup';
import { useEditor } from '@/stores/editor/editor';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import { useColors } from '@/composables/colors';
import Card from './Card.vue';

export default {
  name: 'SetupStep1',

  components: { Card, DpplAutoSizedButton, DpplTextField },
  setup() {
    const { grey400 } = useColors();

    return { grey400 };
  },
  computed: {
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useSettingsSsoSetup, ['name']),

    baseUrl() {
      return window.location.origin;
    },

    metadataUrl() {
      return `${this.baseUrl}/metadata/${this.name}`;
    },

    links() {
      return [
        { label: 'Metadata URL', value: this.metadataUrl },
        { label: 'ACS URL', value: `${this.baseUrl}/acs/${this.name}` },
        { label: 'User Login URL', value: `${this.baseUrl}/login/${this.name}` },
      ];
    },
  },

  methods: {
    ...mapActions(useEditor, ['copyToClipboard']),
  },
};
</script>

<style scoped lang="less">
.active-button {
  pointer-events: all;
  height: 36px;
  width: 36px;
  background: @grey-400;
}

:deep(thead) tr th {
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  color: @text-dark;
}

:deep(table) {
  th, td {
    height: 48px !important;
  }
}

:deep(.v-input){
  color: @text-dark !important;
}

:deep(.v-field) {
  opacity: 1;
  background: @grey-400;

  input {
    font-weight: 300;
  }

  .v-field__outline {
    --v-field-border-width: 1px !important;
    --v-field-border-opacity: .38 !important;
  }
}
</style>
